import { FormikContext } from 'formik';
import React, { useContext } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

interface IFormSubmitButton {
  loading?: boolean;
  children: any;
  className?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  btnType?: 'button' | 'submit' | 'reset';
  detachMode?: boolean;
}

export const FormSubmitButton: React.FC<IFormSubmitButton> = ({
  loading,
  children,
  className,
  disabled,
  onClick,
  btnType,
  detachMode = false,
}) => {
  // Use optional context to allow usage outside Formik without triggering
  // Formik's "useFormikContext() as child of a <Formik>" warning.
  const formikContext = useContext(FormikContext) || {};
  const { dirty, isValid } = formikContext;
  const isDisabled = detachMode && !(isValid && dirty);

  return !isDisabled ? (
    <FormSubmitBtn
      disabled={disabled || loading}
      className={className ? className : ''}
      type={btnType ?? 'submit'}
      onClick={onClick}
    >
      {children}
      {loading && <LoadingOutlined />}
    </FormSubmitBtn>
  ) : null;
};

export const FormSubmitBtn = styled.button`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  font-size: 14px;
  line-height: 14px;
  font-weight: 500;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  color: white;
  background: linear-gradient(
    262.94deg,
    #436db8 9.61%,
    #5b91f5 49.28%,
    #3c80ff 85.91%
  );

  height: fit-content;
  cursor: pointer;

  &.topMargin {
    margin-top: 32px;
  }

  &:hover {
  }

  &:disabled,
  &.disabled:hover {
    color: ${({ theme }) => theme.palette.borderColor};
    border-color: ${({ theme }) => theme.palette.darkBorderColor};
    cursor: not-allowed;
  }

  &.fill {
    width: 100%;
  }

  &.fit {
    width: fit-content;
  }

  &.flex-end {
    align-self: flex-end;
  }

  &.clear {
    background: white;
    color: ${({ theme }) => theme.palette.grayTextColor};
  }

  &.blue-text {
    color: ${({ theme }) => theme.palette.blueColor};
  }

  &.heavy-text {
    font-weight: 600;
  }

  &.auto-left-margin {
    margin-left: auto;
  }

  &.large {
    font-size: 16px;
    line-height: 16px;
    padding: 16px;
    height: 50px;
  }

  &.blue {
    background: linear-gradient(
      262.94deg,
      #436db8 9.61%,
      #5b91f5 49.28%,
      #3c80ff 85.91%
    );
  }

  &.red {
    background: ${({ theme }) => theme.palette.errorRed};
  }

  &.blue-solid {
    background: ${({ theme }) => theme.palette.blueColor};
  }

  &.with-375 {
    width: 375px;
    margin-left: auto;
    margin-right: auto;
  }

  &.width-195 {
    width: 195px;
    margin-left: auto;
    margin-right: auto;
  }

  &.border-blue {
    border: 1px solid ${({ theme }) => theme.palette.blueColor};
  }

  &.color-blue {
    color: ${({ theme }) => theme.palette.blueColor};
  }

  @media (max-width: 884px) {
    font-size: 14px;
    font-weight: 500;
    padding: 12px 18px;

    &.full-mobile {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    font-size: 14px;
    font-weight: 500;
    padding: 12px 16px;

    &.full-mobile {
      width: 100%;
    }

    &.width-195 {
      width: 100%;
      margin-left: 0;
      margin-right: auto;
    }
  }
`;
